import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import { MAPS_API_KEY, MAP_LIBRARIES, MAP_ID, USE_OSM_ONLY, setMapsApiKey } from '../config/maps';
import PlaceAutocomplete, { PlaceSelection as PlaceAutoSelection } from './PlaceAutocomplete';
import LeafletPicker from './LeafletPicker';

type LatLng = { lat: number; lng: number };

export type PlaceSelection = {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  coordinates?: LatLng;
};

interface MapPickerProps {
  value?: PlaceSelection;
  onChange: (next: PlaceSelection) => void;
  height?: number | string;
  defaultCenter?: LatLng;
  disabled?: boolean;
}


function extractAddress(place: google.maps.GeocoderResult | google.maps.places.PlaceResult): PlaceSelection {
  const comps = place.address_components || [];
  const get = (type: string) => comps.find(c => (c.types || []).includes(type))?.long_name || '';
  return {
    name: place.name || undefined,
    address: place.formatted_address || undefined,
    city: get('locality') || get('sublocality') || get('administrative_area_level_2') || undefined,
    state: get('administrative_area_level_1') || undefined,
    country: get('country') || undefined,
    coordinates: place.geometry?.location ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } : undefined,
  };
}

function MapPickerLoaded({ value, onChange, height = 'min(300px,45vh)', defaultCenter, disabled }: MapPickerProps) {
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: MAPS_API_KEY || '', libraries: MAP_LIBRARIES });
  const [center, setCenter] = useState<LatLng>(
    value?.coordinates || defaultCenter || { lat: -1.286389, lng: 36.817223 } // Nairobi default
  );
  const [marker, setMarker] = useState<LatLng | undefined>(value?.coordinates);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [query, setQuery] = useState<string>("");

  useEffect(() => {
    if (value?.coordinates) {
      setCenter(value.coordinates);
      setMarker(value.coordinates);
    }
  }, [value?.coordinates?.lat, value?.coordinates?.lng]);

  const searchAddress = useCallback(() => {
    if (!query.trim()) return;
    if (USE_OSM_ONLY) {
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
        .then((r) => r.json())
        .then((arr) => {
          if (arr && arr[0]) {
            const coords = { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) };
            setCenter(coords);
            setMarker(coords);
            onChange({ ...value, address: arr[0].display_name, coordinates: coords });
          }
        })
        .catch(() => {});
      return;
    }
    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const info = extractAddress(results[0]);
          const loc = results[0].geometry?.location;
          const coords = loc ? { lat: loc.lat(), lng: loc.lng() } : undefined;
          if (coords) {
            setCenter(coords);
            setMarker(coords);
          }
          onChange({ ...value, ...info, coordinates: coords });
        } else {
          // Fallback to OSM if Google Geocoding is disabled/not available
          fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
            .then((r) => r.json())
            .then((arr) => {
              if (arr && arr[0]) {
                const coords = { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) };
                setCenter(coords);
                setMarker(coords);
                onChange({ ...value, address: arr[0].display_name, coordinates: coords });
              }
            })
            .catch(() => {});
        }
      });
    } catch {}
  }, [query, onChange, value]);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setMarker(coords);
    setCenter(coords);
    if (USE_OSM_ONLY) {
      fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}`)
        .then((r) => r.json())
        .then((j) => {
          const address = j?.display_name || '';
          const comps = j?.address || {};
          onChange({
            ...value,
            address,
            city: comps.city || comps.town || comps.village || value?.city,
            state: comps.state || value?.state,
            country: comps.country || value?.country,
            coordinates: coords,
          });
        })
        .catch(() => onChange({ ...value, coordinates: coords }));
      return;
    }
    // Reverse geocode via Google first, fallback to OSM if disabled
    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: coords }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const info = extractAddress(results[0] as any);
          onChange({ ...value, ...info, coordinates: coords });
        } else {
          fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}`)
            .then((r) => r.json())
            .then((j) => {
              const address = j?.display_name || '';
              const comps = j?.address || {};
              onChange({
                ...value,
                address,
                city: comps.city || comps.town || comps.village || value?.city,
                state: comps.state || value?.state,
                country: comps.country || value?.country,
                coordinates: coords,
              });
            })
            .catch(() => onChange({ ...value, coordinates: coords }));
        }
      });
    } catch {
      onChange({ ...value, coordinates: coords });
    }
  }, [onChange, value]);

  const mapOptions = useMemo<google.maps.MapOptions>(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    streetViewControl: false,
    mapId: MAP_ID as any,
  }), []);

  if (loadError || !MAPS_API_KEY) {
    return (
      <LeafletPicker
        value={value}
        onChange={onChange}
        height={height}
        defaultCenter={defaultCenter}
        disabled={disabled}
      />
    );
  }
  if (!isLoaded) {
    return <div className="text-sm text-theme-secondary">Loading map…</div>;
  }

  return (
    <div className="space-y-2">
      {MAPS_API_KEY ? (
        <PlaceAutocomplete
          value={query}
          onSelect={(sel: PlaceAutoSelection) => {
            if (sel.coordinates) {
              setCenter(sel.coordinates);
              setMarker(sel.coordinates);
            }
            onChange({
              ...value,
              name: sel.name || value?.name,
              address: sel.address || value?.address,
              city: sel.city || value?.city,
              state: sel.state || value?.state,
              country: sel.country || value?.country,
              coordinates: sel.coordinates || value?.coordinates,
            });
          }}
        />
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search address (Geocoding)"
            className="input flex-1"
            disabled={disabled}
          />
          <button type="button" className="btn bg-teal-600 text-white" disabled={disabled} onClick={searchAddress}>Find</button>
        </div>
      )}
      <div className="rounded-xl overflow-hidden border" style={{ height, width: '100%', borderColor: 'var(--border)' }}>
        <GoogleMap
          center={center}
          zoom={marker ? 15 : 12}
          mapContainerStyle={{ height: '100%', width: '100%', borderRadius: 12 }}
          options={mapOptions}
          onClick={disabled ? undefined : onMapClick}
          onLoad={(m) => { mapRef.current = m; }}
        >
          {marker && mapRef.current && (
            MAP_ID ? (
              <AdvancedMarker position={marker} map={mapRef.current} />
            ) : (
              <Marker position={marker} />
            )
          )}
        </GoogleMap>
      </div>
    </div>
  );
}

export default function MapPicker(props: MapPickerProps) {
  if (!MAPS_API_KEY) {
    // Fallback to Leaflet/OpenStreetMap when Google key is missing
    return <LeafletPicker {...props} />;
  }
  return <MapPickerLoaded {...props} />;
}

function AdvancedMarker({ position, map }: { position: LatLng; map: google.maps.Map }) {
  const markerRef = useRef<any>(null);

  useEffect(() => {
    if (!map || !position) return;
    try {
      const pin = new (google.maps as any).marker.PinElement();
      const adv = new (google.maps as any).marker.AdvancedMarkerElement({
        map,
        position,
        content: pin.element,
      });
      markerRef.current = adv;
      return () => {
        if (markerRef.current) {
          markerRef.current.map = null;
          markerRef.current = null;
        }
      };
    } catch (e) {
      // If AdvancedMarkerElement is unavailable, do nothing; Google default marker may be created elsewhere
    }
  }, [map]);

  useEffect(() => {
    if (markerRef.current && position) {
      try {
        markerRef.current.position = position;
      } catch {}
    }
  }, [position]);

  return null;
}
